import React, { useState, useEffect, useRef } from 'react'
import { number } from 'prop-types'
import { useTranslation } from 'react-i18next'
import { toastr } from 'components/Toastr'
import { differenceInMilliseconds } from 'date-fns'
import { mutate } from 'swr'
import { useLocalStorage } from 'react-use'
import { datadogRum } from '@datadog/browser-rum'

import { styled } from '@creditas/stylitas'
import { Button as CreditasButton } from '@creditas/button'

import { createAssignment } from 'services/api/salesEngagement/assignments'
import { useAuth } from 'providers/Auth'

import { NoContentFeedback } from './NoContentFeedback'
import { NoTeamFeedback } from './NoTeamFeedback'
import { GenericErrorFeedback } from './GenericErrorFeedback'

const Button = styled(CreditasButton)`
  padding: ${props => (props.small ? '2px 6px' : '10px 16px')};
  ${props =>
    props.small &&
    `
  font-size: 18px;
  margin-right: 10px;
`}
`

const openLinkExternal = businessKey => {
  window.open(
    `${window.location.origin}/auto-refi/${businessKey}/linha-do-tempo`,
    '_blank',
  )
}

const getDiffTimeFromNowInMilliseconds = time => {
  return differenceInMilliseconds(new Date(), new Date(time))
}

const openToastr = timeLeft => {
  toastr.error(<NoContentFeedback timeout={timeLeft} />, {
    autoClose: timeLeft,
  })
}

const SurpriseAction = ({ autoCloseTimeout = 30000, small = false }) => {
  const { t } = useTranslation(['surprise', 'common'])
  const blockSurpriseActionOutOfLifeCycle = useRef(false)
  const [surprising, toggleSurprising] = useState(false)
  const [lastTimeToastrWasShown, setLastTimeToastrWasShown] = useLocalStorage(
    'surprise-last-interaction',
    null,
  )

  const profile = useAuth()

  // This variable helps to know if this component is rendered from a broadcast
  // session o a surprise action. This variable enables avoiding shown two the
  // Toastr when the persisted state changes.
  const [isThisTheBroadcastSession, setThisAsTheBroadcastSession] =
    useState(false)

  useEffect(() => {
    if (lastTimeToastrWasShown === null || isThisTheBroadcastSession) {
      return
    }

    const diffTime = getDiffTimeFromNowInMilliseconds(lastTimeToastrWasShown)

    if (diffTime < autoCloseTimeout) {
      const timeLeft = autoCloseTimeout - diffTime
      openToastr(timeLeft)
      setTimeout(() => {
        setThisAsTheBroadcastSession(false)
        setLastTimeToastrWasShown(null)
      }, timeLeft)
    } else {
      setThisAsTheBroadcastSession(false)
      setLastTimeToastrWasShown(null)
    }
  }, [
    lastTimeToastrWasShown,
    autoCloseTimeout,
    isThisTheBroadcastSession,
    setLastTimeToastrWasShown,
  ])

  const handleNoContentResult = () => {
    setThisAsTheBroadcastSession(true)
    setLastTimeToastrWasShown(new Date())
    openToastr(autoCloseTimeout)
    setTimeout(() => {
      blockSurpriseActionOutOfLifeCycle.current = false
      setThisAsTheBroadcastSession(false)
      setLastTimeToastrWasShown(null)
    }, autoCloseTimeout)
  }

  const handleSurprise = async () => {
    if (blockSurpriseActionOutOfLifeCycle.current) {
      return
    }

    blockSurpriseActionOutOfLifeCycle.current = true
    toggleSurprising(true)

    try {
      const { applicationId } = await createAssignment(profile.team)
      openLinkExternal(applicationId)

      mutate('workflow-details')
      mutate('assignments::counter')
    } catch (error) {
      if (
        error.response &&
        (error.response.status === 422 || error.response.status === 404)
      ) {
        handleNoContentResult()
      } else if (error.response && error.response.status === 403) {
        toastr.info(<NoTeamFeedback />)
      } else if (error.message === 'no_response_content') {
        handleNoContentResult()
      } else {
        datadogRum.addError(error)
        toastr.error(<GenericErrorFeedback />)
      }
    } finally {
      blockSurpriseActionOutOfLifeCycle.current = false
    }

    toggleSurprising(false)
  }

  return (
    <Button
      disabled={lastTimeToastrWasShown !== null}
      isLoading={surprising}
      onClick={handleSurprise}
      small={small}
      fullWidth
    >
      {t('heading.button')}
    </Button>
  )
}

SurpriseAction.propTypes = {
  autoCloseTimeout: number,
}

export { SurpriseAction }
